import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { submissionsApi, type SubmissionListParams } from '@/api/submissions';
import { imagesApi } from '@/api/images';
import { ImageStatus, type Submission } from '@/api/types';

export const submissionKeys = {
  all: ['submissions'] as const,
  lists: () => [...submissionKeys.all, 'list'] as const,
  list: (params: SubmissionListParams) => [...submissionKeys.lists(), params] as const,
  details: () => [...submissionKeys.all, 'detail'] as const,
  detail: (id: string) => [...submissionKeys.details(), id] as const,
};

export function useSubmissions(params: SubmissionListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: submissionKeys.list(params),
    queryFn: () => submissionsApi.list(params),
    enabled: options.enabled ?? true,
  });
}

export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.detail(id ?? ''),
    queryFn: () => submissionsApi.get(id as string),
    enabled: Boolean(id),
  });
}

/** The calling competitor's upload-quota usage per category for a competition. */
export function useSubmissionProgress(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['submissions', 'progress', competitionId ?? ''],
    queryFn: () => submissionsApi.getProgress(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useCreateSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Submission>) => submissionsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['submissions', 'progress'] });
    },
  });
}

export function useUpdateSubmission(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Submission>) => submissionsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: submissionKeys.detail(id) });
    },
  });
}

export function useSubmitSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => submissionsApi.submit(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: submissionKeys.detail(id) });
    },
  });
}

export function useAcceptSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => submissionsApi.accept(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: submissionKeys.detail(id) });
    },
  });
}

export function useRejectSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => submissionsApi.reject(id, reason),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: submissionKeys.detail(id) });
    },
  });
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => submissionsApi.remove(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: submissionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['submissions', 'progress'] });
    },
  });
}

/**
 * Images uploaded for a submission (GET /images?submissionId=). A freshly
 * uploaded image has no resolved URL - watch `status` advance through the
 * async processing pipeline run by Aperture.Workers.
 */
export function useSubmissionImages(submissionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['submissions', submissionId, 'images'] as const,
    queryFn: () => imagesApi.listBySubmission(submissionId as string),
    enabled: Boolean(submissionId) && enabled,
    // The worker produces the previews a few seconds after an upload: keep asking until they exist (or processing failed).
    // An empty list is polled too - the first read can happen before the upload has finished - but only for a few
    // minutes, since a submission may genuinely have no image.
    refetchInterval: (query) => {
      const image = query.state.data?.items[0];
      if (image && (image.thumbnailUrl || image.previewUrl || image.status === ImageStatus.Failed || image.status === ImageStatus.ValidationFailed)) {
        return false;
      }
      return query.state.dataUpdateCount < 60 ? 3000 : false;
    },
  });
}

/** DELETE /images/{id} - see imagesApi.remove for the ownership/edit-window rules it enforces server-side. */
export function useDeleteImage(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) => imagesApi.remove(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions', submissionId, 'images'] });
    },
  });
}
